import React, { useEffect, useState } from 'react';
import {
  isUserPreferencesRequestError,
  type UserPreferencesErrorInfo,
} from '../../context/UserPreferencesContext';
import {
  getLocaleLabelKey,
  isSupportedUiLocale,
  SUPPORTED_UI_LOCALES,
  type SupportedUiLocale,
} from '../../localization/locales';
import { useTranslation } from '../../localization/useAppTranslation';
import { SearchableDropdown } from '../SearchableDropdown';
import {
  type UserPreferenceKey,
  type UserPreferences,
} from '../../types/preferences';

type PreferenceDraftValue = SupportedUiLocale | '';

interface ControlState {
  isSaving: boolean;
  error: string | null;
  retryValue: PreferenceDraftValue | null;
}

interface UserSettingsPanelProps {
  preferences: UserPreferences | null;
  isLoading: boolean;
  loadError: UserPreferencesErrorInfo | null;
  onRetryLoad: () => Promise<UserPreferences | null>;
  onSavePreference: (
    preferenceKey: UserPreferenceKey,
    value: SupportedUiLocale | null,
  ) => Promise<UserPreferences>;
  onSavingStateChange?: (isSaving: boolean) => void;
}

type PreferenceDrafts = Record<UserPreferenceKey, PreferenceDraftValue>;
type PreferenceControlStates = Record<UserPreferenceKey, ControlState>;
const RESPONSE_LOCALE_PREFERENCE_KEY: UserPreferenceKey = 'response_locale';
const UI_LOCALE_PREFERENCE_KEY: UserPreferenceKey = 'ui_locale';

const createControlState = (): ControlState => ({
  isSaving: false,
  error: null,
  retryValue: null,
});

const createControlStates = (): PreferenceControlStates => ({
  ui_locale: createControlState(),
  response_locale: createControlState(),
});

const preferencesToDrafts = (preferences: UserPreferences): PreferenceDrafts => ({
  ui_locale: preferences.ui_locale || '',
  response_locale: preferences.response_locale || '',
});

export const UserSettingsPanel: React.FC<UserSettingsPanelProps> = ({
  preferences,
  isLoading,
  loadError,
  onRetryLoad,
  onSavePreference,
  onSavingStateChange,
}) => {
  const { t } = useTranslation('preferences');
  const [drafts, setDrafts] = useState<PreferenceDrafts>(() => preferencesToDrafts({
    ui_locale: null,
    response_locale: null,
  }));
  const [controlStates, setControlStates] = useState<PreferenceControlStates>(createControlStates);
  const localeOptions = SUPPORTED_UI_LOCALES.map((locale) => ({
    value: locale,
    label: t(getLocaleLabelKey(locale)),
  }));

  useEffect(() => {
    if (!preferences) {
      return;
    }

    setDrafts((currentDrafts) => ({
      ui_locale: controlStates.ui_locale.isSaving
        ? currentDrafts.ui_locale
        : preferences.ui_locale || '',
      response_locale: controlStates.response_locale.isSaving
        ? currentDrafts.response_locale
        : preferences.response_locale || '',
    }));
  }, [preferences]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSaving = Object.values(controlStates).some((state) => state.isSaving);

  useEffect(() => {
    onSavingStateChange?.(isSaving);
  }, [isSaving, onSavingStateChange]);

  useEffect(() => () => {
    onSavingStateChange?.(false);
  }, [onSavingStateChange]);

  const savePreference = async (
    preferenceKey: UserPreferenceKey,
    nextValue: PreferenceDraftValue,
  ) => {
    const confirmedValue = preferences?.[preferenceKey] || '';
    setDrafts((currentDrafts) => ({ ...currentDrafts, [preferenceKey]: nextValue }));
    setControlStates((currentStates) => ({
      ...currentStates,
      [preferenceKey]: { isSaving: true, error: null, retryValue: null },
    }));

    try {
      await onSavePreference(preferenceKey, nextValue || null);
      setControlStates((currentStates) => ({
        ...currentStates,
        [preferenceKey]: createControlState(),
      }));
    } catch (error) {
      const errorMessage = isUserPreferencesRequestError(error)
        ? t('errors.saveFailed')
        : error instanceof Error
          ? error.message
          : t('errors.saveFailed');
      setDrafts((currentDrafts) => ({ ...currentDrafts, [preferenceKey]: confirmedValue }));
      setControlStates((currentStates) => ({
        ...currentStates,
        [preferenceKey]: {
          isSaving: false,
          error: errorMessage,
          retryValue: nextValue,
        },
      }));
    }
  };

  if (!preferences) {
    if (loadError) {
      return (
        <div className="settings-panel-state" role="alert">
          <p>{t('errors.loadFailed')}</p>
          <button type="button" className="settings-retry-button" onClick={() => void onRetryLoad()}>
            {t('common:actions.retry')}
          </button>
        </div>
      );
    }

    return null;
  }

  const renderLocaleControl = (
    preferenceKey: UserPreferenceKey,
    label: string,
    defaultLabel: string,
    helpText: string,
  ) => {
    const controlState = controlStates[preferenceKey];
    const controlId = `settings-${preferenceKey}`;
    const errorId = `${controlId}-error`;

    return (
      <div className="settings-control" key={preferenceKey}>
        <label htmlFor={controlId} title={helpText}>{label}</label>
        <SearchableDropdown
          id={controlId}
          className="settings-dropdown"
          options={[
            { value: '', label: defaultLabel },
            ...localeOptions,
          ]}
          value={drafts[preferenceKey]}
          placeholder={defaultLabel}
          title={helpText}
          ariaLabel={label}
          onChange={(value) => {
            if (value === '' || isSupportedUiLocale(value)) {
              void savePreference(preferenceKey, value);
            }
          }}
          disabled={controlState.isSaving}
          ariaDescribedBy={controlState.error ? errorId : undefined}
        />
        {controlState.error && (
          <div id={errorId} className="settings-control-error" role="alert">
            <span>{controlState.error}</span>
            <button
              type="button"
              className="settings-retry-button"
              onClick={() => void savePreference(preferenceKey, controlState.retryValue ?? '')}
            >
              {t('common:actions.retry')}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <section
      className="sidebar-panel-scroll settings-panel"
      aria-label={t('settings.ariaLabel')}
    >
      <div className="settings-controls">
        {renderLocaleControl(
          UI_LOCALE_PREFERENCE_KEY,
          t('appLanguage.label'),
          t('appLanguage.useDeviceLanguage'),
          t('appLanguage.helpText'),
        )}
        {renderLocaleControl(
          RESPONSE_LOCALE_PREFERENCE_KEY,
          t('assistantReplies.label'),
          t('assistantReplies.matchAppLanguage'),
          t('assistantReplies.helpText'),
        )}
      </div>
    </section>
  );
};